import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiBell, FiMoon, FiSun, FiUser, FiMail, FiUserPlus, FiLogOut, FiSearch, FiMessageSquare, FiUsers, FiShield } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const { currentUser, userStatus, toggleStatus, logout, chats, notifications, unreadNotifications, friends = [], receivedRequests = [], markNotificationsAsRead, isDarkMode, toggleDarkMode } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) setShowNotifs(false);
      if (profileRef.current && !profileRef.current.contains(event.target)) setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentUser) return null;

  const isAdmin = currentUser.role === 'admin';
  const unreadChatsCount = (chats || []).filter(chat => friends.includes(chat.user?.id) && (chat.unread || 0) > 0).length;
  const pendingRequestsCount = receivedRequests.filter(reqId => !friends.includes(reqId)).length;
  const totalBadgeCount = unreadChatsCount + pendingRequestsCount;
  const isInsideChat = location.pathname.match(/\/messages\/c\d+/);

  return (
    <>
      <nav className="bg-white/80 dark:bg-dark-bg/80 backdrop-blur-md border-b border-gray-100 dark:border-dark-border sticky top-0 z-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group outline-none">
            <div className="w-9 h-9 bg-brand-600 rounded-xl flex items-center justify-center group-hover:rotate-6 transition-transform shadow-lg shadow-brand-200 dark:shadow-brand-900/20">
              <span className="text-white font-black text-xl leading-none tracking-tighter">N</span>
            </div>
            <span className="font-black text-xl text-brand-950 dark:text-white tracking-tight">Nakama</span>
          </Link>

          <div className="hidden sm:flex items-center gap-8">
            <NavLink to="/" className={({ isActive }) => `font-bold text-sm uppercase tracking-widest transition-colors ${isActive ? 'text-brand-600' : 'text-gray-500 dark:text-gray-400 hover:text-brand-600'}`}>Discovery</NavLink>
            <NavLink to="/messages" className={({ isActive }) => `font-bold text-sm uppercase tracking-widest flex items-center gap-2 transition-colors ${isActive ? 'text-brand-600' : 'text-gray-500 dark:text-gray-400 hover:text-brand-600'}`}>
              Messages 
              {totalBadgeCount > 0 && (
                <span className="bg-brand-600 text-white text-[10px] rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center shadow-md animate-pulse font-black">
                  {totalBadgeCount}
                </span>
              )}
            </NavLink>
            {isAdmin && (
              <NavLink to="/admin" className={({ isActive }) => `font-bold text-sm uppercase tracking-widest flex items-center gap-2 transition-colors ${isActive ? 'text-amber-600' : 'text-gray-500 dark:text-gray-400 hover:text-amber-600'}`}>
                <SafeIcon icon={FiShield} className="w-4 h-4" /> Admin Panel
              </NavLink>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleDarkMode} className="p-2.5 rounded-xl transition-all text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-surface outline-none">
              <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="w-5 h-5" />
            </button>

            <div className="relative" ref={notifRef}>
              <button onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) markNotificationsAsRead(); }} className={`p-2.5 rounded-xl transition-all relative outline-none ${showNotifs ? 'bg-brand-50 dark:bg-dark-surface text-brand-600' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-surface'}`}>
                <SafeIcon icon={FiBell} className="w-5 h-5" />
                {unreadNotifications > 0 && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 border-2 border-white dark:border-dark-bg rounded-full"></span>}
              </button>
              <AnimatePresence>
                {showNotifs && (
                  <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="fixed left-4 right-4 top-[72px] mx-auto max-w-[360px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-3 sm:w-80 sm:mx-0 bg-white dark:bg-dark-surface rounded-3xl shadow-2xl border border-gray-100 dark:border-dark-border overflow-hidden z-[60] origin-top sm:origin-top-right">
                    <div className="p-4 border-b border-gray-50 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface/50 flex justify-between items-center">
                      <h3 className="font-black text-brand-950 dark:text-white text-xs uppercase tracking-widest">Notifications</h3>
                      <button onClick={markNotificationsAsRead} className="text-[10px] font-bold text-brand-600 hover:underline">Mark as read</button>
                    </div>
                    <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                      {notifications.length > 0 ? (
                        notifications.map(n => (
                          <div key={n.id} onClick={() => { setShowNotifs(false); navigate(n.type === 'request' ? `/profile/${n.user.id}` : '/messages'); }} className={`p-4 flex gap-3 border-b border-gray-50 dark:border-dark-border last:border-0 hover:bg-brand-50 dark:hover:bg-dark-card transition-colors cursor-pointer ${!n.read ? 'bg-brand-50/30 dark:bg-brand-900/10' : ''}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.type === 'request' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'bg-brand-100 dark:bg-brand-900/30 text-brand-600'}`}>
                              <SafeIcon icon={n.type === 'request' ? FiUserPlus : FiMail} className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-brand-950 dark:text-gray-200 leading-snug"><span className="font-black">{n.user.nickname}</span> {n.message}</p>
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold mt-1">{n.time}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">Peace and quiet</div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative" ref={profileRef}>
              <button onClick={() => setShowProfileMenu(!showProfileMenu)} className="relative p-1 rounded-2xl hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors flex items-center gap-2 outline-none">
                <img src={currentUser.avatar} alt="Profile" className="w-9 h-9 rounded-xl border border-gray-100 dark:border-dark-border object-cover shadow-sm" />
                <span className={`absolute -bottom-1 -right-1 w-4 h-4 border-2 border-white dark:border-dark-bg rounded-full ${userStatus === 'online' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              </button>
              <AnimatePresence>
                {showProfileMenu && (
                  <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute right-0 mt-3 w-56 bg-white dark:bg-dark-surface rounded-2xl shadow-2xl border border-gray-100 dark:border-dark-border p-2 z-[60] origin-top-right">
                    <div className="p-3 border-b border-gray-50 dark:border-dark-border mb-2">
                      <p className="font-black text-brand-950 dark:text-white text-sm truncate">{currentUser.nickname}</p>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isAdmin ? 'text-amber-600' : 'text-gray-400'}`}>
                        {isAdmin ? 'Administrator' : 'Player'}
                      </p>
                    </div>
                    <button onClick={toggleStatus} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-brand-50 dark:hover:bg-dark-card transition-colors text-sm font-bold text-gray-700 dark:text-gray-300">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${userStatus === 'online' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                        <span>{userStatus === 'online' ? 'Online' : 'Invisible'}</span>
                      </div>
                      <SafeIcon icon={userStatus === 'online' ? FiSun : FiMoon} className={userStatus === 'online' ? 'text-yellow-500' : 'text-gray-400'} />
                    </button>
                    <button onClick={() => { navigate(`/profile/${currentUser.id}`); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-brand-50 dark:hover:bg-dark-card transition-colors text-sm font-bold text-gray-700 dark:text-gray-300">
                      <SafeIcon icon={FiUser} className="text-gray-400" /> Profile
                    </button>
                    {isAdmin && (
                      <button onClick={() => { navigate('/admin'); setShowProfileMenu(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors text-sm font-bold text-amber-600">
                        <SafeIcon icon={FiShield} /> Admin Panel
                      </button>
                    )}
                    <div className="border-t border-gray-50 dark:border-dark-border mt-2 pt-2">
                      <button onClick={logout} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors text-sm font-bold">
                        <SafeIcon icon={FiLogOut} /> Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}